import React from "react";
import { motion } from "framer-motion";
import { FaFacebook, FaPhone, FaClock, FaMapMarkerAlt } from "react-icons/fa";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { useTranslation } from 'react-i18next';
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon from "../pics/markerIcon.png";

const defaultCenter = { lat: 13.86893, lng: 100.57914 };

const icon = new L.Icon({
  iconUrl: markerIcon,
  iconSize: [18, 25],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

function ContactNew() {
  const { i18n } = useTranslation();

  const contactInfo = {
    address: {
      th: "เลขที่ 191 ซอยวิภาวดี 62 (แยก 4-7) ถนนวิภาวดีรังสิต แขวงตลาดบางเขน เขตหลักสี่ กรุงเทพมหานคร 10210",
      en: "191 Soi Vibhavadi 62 (Yaek 4-7), Vibhavadi Rangsit Road, Talat Bang Khen, Lak Si, Bangkok 10210"
    },
    hours: {
      weekday: i18n.language === 'en' ? "Mon-Fri: 09:30 - 17:00" : "วันจันทร์-ศุกร์: 09.30 - 17.00 น.",
      weekend: i18n.language === 'en' ? "Sat-Sun & Holidays: 10:00 - 16:00" : "วันเสาร์-อาทิตย์ และวันหยุดนักขัตฤกษ์: 10.00 - 16.00 น."
    },
    phone: "061-909-1840",
    social: {
      facebook: "https://www.facebook.com/sharingmirrorfoundation?locale=th_TH"
    }
  };

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'linear-gradient(135deg, #e0f7fa 0%, #f0f9ff 50%, #e1f5fe 100%)' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        padding: '5rem 0 4rem',
        marginBottom: '3rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Decorative Elements */}
        <motion.div
          style={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            fontSize: '6rem',
            opacity: '0.15'
          }}
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >📞</motion.div>
        <motion.div
          style={{
            position: 'absolute',
            bottom: '20%',
            left: '8%',
            fontSize: '4rem',
            opacity: '0.15'
          }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, -5, 5, 0]
          }}
          transition={{ 
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >💬</motion.div>
        <motion.div
          style={{
            position: 'absolute',
            top: '50%',
            right: '15%',
            fontSize: '3rem',
            opacity: '0.12'
          }}
          animate={{ 
            y: [0, -15, 0],
            x: [0, 10, 0]
          }}
          transition={{ 
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >📧</motion.div>
        
        <div className="container-modern text-center" style={{ position: 'relative' }}>
          <motion.div
            style={{
              fontSize: '4rem',
              marginBottom: '1rem'
            }}
            animate={{ 
              rotate: [0, 15, -15, 0]
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              repeatDelay: 1
            }}
          >
            👋
          </motion.div>
          
          <motion.h1
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: '800',
              color: 'white',
              marginBottom: '1rem',
              textShadow: '0 2px 20px rgba(0,0,0,0.2)'
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {i18n.language === 'en' ? 'Let\'s Connect & Make a Difference' : 'ติดต่อเราเพื่อร่วมสร้างความดี'}
          </motion.h1>
          <motion.p
            style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: 'rgba(255, 255, 255, 0.95)',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: '1.8'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {i18n.language === 'en'
              ? 'Together we can change lives. Have questions about donations or volunteering? We\'re here to help!'
              : 'เราพร้อมร่วมสร้างความดีกับคุณ มีคำถามเกี่ยวกับการบริจาคหรืออาสาสมัคร? ติดต่อเราได้เลย!'}
          </motion.p>
        </div>
      </section>

      <div className="container-modern" style={{ paddingBottom: '4rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {/* Address Card */}
          <motion.div
            style={{
              background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
              borderRadius: '20px',
              padding: '2rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ y: -8, boxShadow: '0 20px 25px -5px rgba(79,172,254,0.3)' }}
          >
            <motion.div
              style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.3)',
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1.25rem',
              boxShadow: '0 8px 16px rgba(79, 195, 247, 0.4)',
              position: 'relative'
            }}>
              <FaMapMarkerAlt style={{ fontSize: '1.5rem', color: 'white' }} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1a202c', marginBottom: '1rem' }}>
              {i18n.language === 'en' ? '📍 Our Location' : '📍 ที่ตั้งของเรา'}
            </h3>
            <p style={{ color: '#37474f', lineHeight: '1.7', fontSize: '0.95rem', position: 'relative' }}>
              {contactInfo.address[i18n.language] || contactInfo.address.th}
            </p>
          </motion.div>

          {/* Hours Card */}
          <motion.div
            style={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              borderRadius: '20px',
              padding: '2rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -8, boxShadow: '0 20px 25px -5px rgba(66,165,245,0.3)' }}
          >
            <motion.div
              style={{
                position: 'absolute',
                top: '50%',
                right: '-30px',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.4)',
              }}
              animate={{
                rotate: [0, 360]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #42a5f5 0%, #1e88e5 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1.25rem',
              boxShadow: '0 8px 16px rgba(66, 165, 245, 0.4)',
              position: 'relative'
            }}>
              <FaClock style={{ fontSize: '1.5rem', color: 'white' }} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1a202c', marginBottom: '1rem' }}>
              {i18n.language === 'en' ? '⏰ Opening Hours' : '⏰ เวลาทำการ'}
            </h3>
            <div style={{ color: '#37474f', lineHeight: '1.8', fontSize: '0.95rem', position: 'relative' }}>
              <div style={{ 
                marginBottom: '0.75rem',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.6)',
                borderRadius: '12px',
                fontWeight: '500'
              }}>
                {contactInfo.hours.weekday}
              </div>
              <div style={{ 
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.4)',
                borderRadius: '12px',
                color: '#546e7a',
                fontSize: '0.9rem'
              }}>
                {contactInfo.hours.weekend}
              </div>
            </div>
          </motion.div>

          {/* Contact Card */}
          <motion.div
            style={{
              background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)',
              borderRadius: '20px',
              padding: '2rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ y: -8, boxShadow: '0 20px 25px -5px rgba(3,169,244,0.3)' }}
          >
            <motion.div
              style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '140px',
                height: '140px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.35)',
              }}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #03a9f4 0%, #0288d1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1.25rem',
              boxShadow: '0 8px 16px rgba(3, 169, 244, 0.4)',
              position: 'relative'
            }}>
              <FaPhone style={{ fontSize: '1.5rem', color: 'white' }} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1a202c', marginBottom: '1rem' }}>
              {i18n.language === 'en' ? '☎️ Call Us' : '☎️ โทรหาเรา'}
            </h3>
            <a 
              href={`tel:${contactInfo.phone}`} 
              style={{ 
                display: 'inline-block',
                color: 'white',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '1.1rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 8px 16px rgba(79, 172, 254, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.3)';
              }}
            >
              📞 {contactInfo.phone}
            </a>
          </motion.div>
        </div>

        {/* Map */}
        <motion.div
          style={{
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            height: 'clamp(300px, 40vh, 500px)',
            marginBottom: '3rem'
          }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <MapContainer center={defaultCenter} zoom={14} style={{ width: "100%", height: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={defaultCenter} icon={icon}>
              <Popup>
                <strong>{i18n.language === 'en' ? 'We are here!' : 'เราอยู่ที่นี่!'}</strong>
              </Popup>
            </Marker>
          </MapContainer>
        </motion.div>

        {/* Social Media */}
        <motion.div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '24px',
            padding: '3rem',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            filter: 'blur(40px)'
          }} />
          
          <div style={{ position: 'relative' }}>
            <motion.div
              style={{ fontSize: '3rem', marginBottom: '1rem' }}
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
            >
              💙
            </motion.div>
            
            <h3 style={{ fontSize: '2rem', fontWeight: '700', color: 'white', marginBottom: '0.75rem' }}>
              {i18n.language === 'en' ? 'Stay Connected' : 'ติดตามเราได้ที่'}
            </h3>
            <p style={{
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '2rem',
              fontSize: '1rem'
            }}>
              {i18n.language === 'en' 
                ? 'Follow us for updates, stories, and more!'
                : 'ติดตามเพื่อรับข่าวสาร เรื่องราว และอื่นๆ อีกมากมาย!'}
            </p>

            <motion.a
              href={contactInfo.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 2.5rem',
                background: 'white',
                color: '#1877f2',
                borderRadius: '16px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                transition: 'all 0.3s'
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 20px 25px -5px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
              }}
            >
              <FaFacebook size={28} />
              <span>{i18n.language === 'en' ? 'Follow on Facebook' : 'ติดตามบน Facebook'}</span>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ContactNew;
